import _ from 'underscore';
import React from 'react';
import PropTypes from 'prop-types';
import { Virtuoso } from 'react-virtuoso';

import Icon from 'components/Icon';
import Preloader from 'components/Preloader';

import { DEFAULT_LIMIT } from 'utils/helpers/constants';
import ListItem from '../Item';

import styles from './styles.module.scss';

const LIST_ITEM_HEIGHT = 64;
const MAX_LIST_HEIGHT = 512;
const MAX_ITEMS_LIMIT = 7;

const getHeight = ({
	optionsLength,
	isAnyOptionSelected,
	notFound,
	isLoading,
	isLoadingMore
}) => {
	const truthyFields = Object.values({
		isAnyOptionSelected,
		notFound,
		isLoading,
		isLoadingMore
	}).filter(Boolean);

	const optionsLengthWithSelectedOption =
		(isLoading ? 0 : optionsLength) + truthyFields.length;

	if (optionsLengthWithSelectedOption >= MAX_ITEMS_LIMIT) {
		return MAX_LIST_HEIGHT - LIST_ITEM_HEIGHT;
	}
	return optionsLengthWithSelectedOption * LIST_ITEM_HEIGHT;
};

const CustomSelectList = ({
	isLoading,
	selectedValue,
	options,
	onChange,
	searchable,
	resetOption,
	onSearch,
	placeholder,
	searchValue,
	emptyMessage,
	valueField,
	loadMore,
	isLoadingMore
}) => {
	const optionsIsEmpty = _.isEmpty(options);

	const notFound = searchValue.length && optionsIsEmpty && !isLoading;

	const isAnyOptionSelected =
		resetOption && selectedValue !== resetOption[valueField];

	const listHeight = getHeight({
		optionsLength: options?.length,
		isAnyOptionSelected,
		notFound,
		isLoading,
		isLoadingMore
	});

	return (
		<div className={styles.CustomSelectList}>
			{searchable && (
				<div className={styles.CustomSelectList__SearchWrapper}>
					<Icon icon="search" className={styles.CustomSelectList__SearchIcon} />

					<input
						className={styles.CustomSelectList__SearchInput}
						placeholder={placeholder}
						onChange={onSearch}
						value={searchValue}
					/>
				</div>
			)}

			{isLoading ? (
				<Preloader className={styles.CustomSelectList__Preloader} />
			) : (
				<div className={styles.CustomSelectList__Content}>
					<Virtuoso
						className={styles.CustomSelectList__VirtuozoContainer}
						style={{ height: listHeight }}
						data={options}
						totalCount={options.length}
						endReached={(index) => {
							const hasMore = (index + 1) % DEFAULT_LIMIT === 0;
							if (hasMore) {
								loadMore();
							}
						}}
						overscan={200}
						itemContent={(index, data) => {
							const { title, titleGenitive } = data;
							const value = data[valueField];

							return (
								<ListItem
									key={index}
									value={value}
									title={title}
									active={_.isEqual(selectedValue, value)}
									titleGenitive={titleGenitive}
									onChange={onChange}
								/>
							);
						}}
						components={{
							Header: () => (
								<>
									{isAnyOptionSelected ? (
										<ListItem
											value={resetOption[valueField]}
											active={_.isEqual(selectedValue, resetOption[valueField])}
											title={resetOption.title}
											onChange={onChange}
										/>
									) : null}
									{notFound ? (
										<div className={styles.CustomSelectList__NotFound}>
											{emptyMessage}
										</div>
									) : null}
								</>
							),
							Footer: () =>
								isLoadingMore ? (
									<Preloader className={styles.CustomSelectList__Preloader} />
								) : null
						}}
					/>
				</div>
			)}
		</div>
	);
};

CustomSelectList.propTypes = {
	isLoading: PropTypes.bool.isRequired,
	options: PropTypes.arrayOf(
		PropTypes.shape({
			title: PropTypes.string.isRequired,
			value: PropTypes.string
		})
	).isRequired,
	resetOption: PropTypes.shape({
		title: PropTypes.string.isRequired,
		value: PropTypes.string
	}),
	onChange: PropTypes.func,
	selectedValue: PropTypes.node.isRequired,
	searchable: PropTypes.bool,
	onSearch: PropTypes.func,
	placeholder: PropTypes.string,
	searchValue: PropTypes.string,
	emptyMessage: PropTypes.string,
	valueField: PropTypes.string.isRequired,
	loadMore: PropTypes.func,
	isLoadingMore: PropTypes.bool
};

CustomSelectList.defaultProps = {
	onChange: _.noop,
	searchable: false,
	placeholder: 'Введите для поиска',
	onSearch: _.noop,
	emptyMessage: 'Ничего не найдено',
	isLoadingMore: false
};

export default CustomSelectList;
